
import { fetchTasks, postNewTask, postUpdateTask } from "@/helpers/api";
import { Task } from "@/interfaces/Task";
import { defineComponent, toRefs, reactive, ref, computed, watch, PropType } from "vue";
import Datepicker from "vue3-datepicker";
import VueFeather from "vue-feather";
import { TaskRule } from "@/interfaces/TaskRule";
import { jsTimeToRealEpoch, timeAsJS, getNextDayFromDate, timeNowAsJS } from "@/helpers/time";
import { getDateFormat } from "@/helpers/store";
import TagMultiPicker from "./TagMultiPicker.vue";
import JoinConfigSection from "./JoinConfigSection.vue";
import {
  JoinConfigDraft,
  ParentDraft,
  ParentKind,
  PARENT_KIND_OPTIONS,
  TagActionRowDraft,
  TagSetDraft,
  TAG_ACTION_OPTIONS,
} from "./tasksConfigTypes";

// Tag key regex must match ga11ium.tasks.join_config._TAG_KEY_RE — the backend
// interpolates these into SQL, so we validate before send.
const JOIN_CONFIG_TAG_KEY_RE = /^[A-Za-z0-9_]+$/;

const buildJoinDefaults = (): JoinConfigDraft => ({
  filter_to_same: [],
  timeout_minutes: null,
  min_success_fraction: null,
  share_grandparent: false,
  grandparent_timeout_minutes: null,
  retry_grace_minutes: null,
});

const normalizeSensorTags = (sensorTags: Record<string, string> | Record<string, string>[] | null): TagSetDraft[] => {
  if (!sensorTags || (!Array.isArray(sensorTags) && Object.keys(sensorTags).length === 0)) {
    return [{ tags: {}, selectTag: "", selectTagValue: "" }];
  }
  if (Array.isArray(sensorTags)) {
    return sensorTags.map((tags) => ({ tags: { ...tags }, selectTag: "", selectTagValue: "" }));
  }
  return [{ tags: { ...sensorTags }, selectTag: "", selectTagValue: "" }];
};

const loadTagActions = (raw: unknown): TagActionRowDraft[] => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  const out: TagActionRowDraft[] = [];
  for (const [tag, spec] of Object.entries(raw as Record<string, unknown>)) {
    if (!spec || typeof spec !== "object") continue;
    const action = (spec as Record<string, unknown>).action as TagActionRowDraft["action"];
    if (!TAG_ACTION_OPTIONS.includes(action)) continue;
    const row: TagActionRowDraft = { tag, action, pn: "" };
    if (action === "match") {
      const pn = (spec as Record<string, unknown>).pn;
      row.pn = typeof pn === "number" && Number.isInteger(pn) && pn > 0 ? pn : "";
    }
    out.push(row);
  }
  return out;
};

const loadParents = (raw: unknown): { parents: ParentDraft[]; dropped: string[] } => {
  const dropped: string[] = [];
  if (!Array.isArray(raw)) {
    if (raw !== undefined && raw !== null) {
      dropped.push("parents config was not an array; starting with an empty parent list");
    }
    return { parents: [], dropped };
  }
  const out: ParentDraft[] = [];
  for (const [idx, entry] of (raw as Array<Record<string, unknown>>).entries()) {
    if (!entry || typeof entry !== "object") {
      dropped.push(`parents[${idx}]: not an object`);
      continue;
    }
    const id = entry.id;
    if (typeof id !== "number" || !Number.isInteger(id) || id <= 0) {
      dropped.push(`parents[${idx}]: id missing or not a positive integer`);
      continue;
    }
    const kind = entry.kind as ParentKind;
    if (!PARENT_KIND_OPTIONS.includes(kind)) {
      dropped.push(`parents[${idx}] (id=${String(id)}): unknown kind "${String(entry.kind)}"`);
      continue;
    }
    const trid = entry.task_rule_id;
    out.push({
      id,
      kind,
      task_rule_id:
        kind === "task_rule" && typeof trid === "number" && Number.isInteger(trid) && trid > 0 ? trid : null,
      tagSets: normalizeSensorTags(entry.sensor_tags as Record<string, string> | Record<string, string>[] | null),
      tagActions: loadTagActions(entry.tag_actions),
    });
  }
  return { parents: out, dropped };
};

// Split the join-config keys out of Parameters into typed drafts, leaving everything
// else (task-function kwargs or forward-compat keys) in `extras` so save_rule can put
// them back unchanged.
const loadJoinConfig = (
  parameters: Record<string, unknown> | null | undefined
): {
  draft: JoinConfigDraft;
  parents: ParentDraft[];
  extras: Record<string, unknown>;
  droppedParentsWarnings: string[];
} => {
  const draft = buildJoinDefaults();
  let parents: ParentDraft[] = [];
  let droppedParentsWarnings: string[] = [];
  const extras: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(parameters ?? {})) {
    switch (k) {
      case "parents": {
        const loaded = loadParents(v);
        parents = loaded.parents;
        droppedParentsWarnings = loaded.dropped;
        break;
      }
      case "filter_to_same":
        draft.filter_to_same = Array.isArray(v) ? (v as unknown[]).map(String) : [];
        break;
      case "timeout_minutes":
        draft.timeout_minutes = typeof v === "number" ? v : null;
        break;
      case "min_success_fraction":
        draft.min_success_fraction = typeof v === "number" ? v : null;
        break;
      case "share_grandparent":
        draft.share_grandparent = Boolean(v);
        break;
      case "grandparent_timeout_minutes":
        draft.grandparent_timeout_minutes = typeof v === "number" ? v : null;
        break;
      case "retry_grace_minutes":
        draft.retry_grace_minutes = typeof v === "number" ? v : null;
        break;
      default:
        extras[k] = v;
    }
  }
  return { draft, parents, extras, droppedParentsWarnings };
};

const buildParentDraft = (id: number, kind: ParentKind = "task_rule"): ParentDraft => ({
  id,
  kind,
  task_rule_id: null,
  tagSets: [{ tags: {}, selectTag: "", selectTagValue: "" }],
  tagActions: [],
});

// Monotonic parent-id generator — holds the highest id ever issued for this modal
// session so removing a parent and adding a new one never resurrects an id that was
// just deleted. A Match(pn) reference to the deleted parent would then silently
// repoint at the new one; keeping ids monotonic makes that impossible.
const buildParentIdCounter = (initialMax: number): { next: () => number } => {
  let n = initialMax;
  return {
    next: () => {
      n += 1;
      return n;
    },
  };
};

const highestParentId = (parents: ParentDraft[]): number =>
  parents.length === 0 ? 0 : Math.max(...parents.map((p) => p.id));

// Dynamic rules group on filter_to_same only, so their broadcast rows are left out of the save.
const serializeParent = (p: ParentDraft, dynamic: boolean): Record<string, unknown> => {
  const nonEmpty = p.tagSets.map((s) => s.tags).filter((t) => Object.keys(t).length > 0);
  const sensorTags = nonEmpty.length === 0 ? null : nonEmpty.length === 1 ? nonEmpty[0] : nonEmpty;
  const tagActions: Record<string, unknown> = {};
  for (const row of dynamic ? [] : p.tagActions) {
    if (!row.tag) continue;
    if (row.action === "match") {
      tagActions[row.tag] = { action: "match", pn: Number(row.pn) };
    } else {
      tagActions[row.tag] = { action: row.action };
    }
  }
  const out: Record<string, unknown> = { id: p.id, kind: p.kind };
  if (p.kind === "task_rule" && p.task_rule_id !== null) out.task_rule_id = p.task_rule_id;
  if (sensorTags !== null) out.sensor_tags = sensorTags;
  if (Object.keys(tagActions).length > 0) out.tag_actions = tagActions;
  return out;
};

export default defineComponent({
  name: "tasks-config-modal",
  components: {
    Datepicker,
    VueFeather,
    TagMultiPicker,
    JoinConfigSection,
  },
  props: {
    selected_rule: {
      type: Object as PropType<TaskRule>,
      required: true,
    },
    sensorTagOptions: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    task_rules: {
      type: Object as PropType<TaskRule[]>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const initialJoin = loadJoinConfig(props.selected_rule.Parameters as unknown as Record<string, unknown> | null);
    const state = reactive({
      Name: props.selected_rule.Name,
      errorMessage: "",
      // Non-blocking messages. Starts with any parent entries that failed to load (so the user
      // sees what a save would drop); replaced on save by the write endpoint's
      // `200 {"warnings": [...]}` (disabled parents, missing sensor coverage).
      warnings: initialJoin.droppedParentsWarnings.map((w) => `Loaded join config: dropped ${w}`),
      TaskID: props.selected_rule.TaskID,
      sourceData: props.selected_rule.InputTable,
      pickerStartDate: props.selected_rule.StartTime
        ? ref(timeAsJS(props.selected_rule.StartTime))
        : ref(timeNowAsJS()),
      pickerEndDate: props.selected_rule.EndTime ? ref(timeAsJS(props.selected_rule.EndTime)) : null,
      endDate: (props.selected_rule.EndTime ? true : false) || false,
      enabled: props.selected_rule.Enabled,
      multipleTruePerBatch: props.selected_rule.MultipleTruePerBatch ?? true,
      tasks: [] as Task[],
      tagSets: normalizeSensorTags(props.selected_rule.SensorTags),
      sensorTagOptions: props.sensorTagOptions as Record<string, string[]>,
      DataTaskRuleIDs: props.selected_rule.DataTaskRuleIDs,
      maxConcurrent: (props.selected_rule.MaxConcurrent ?? null) as number | string | null,
      task_rules: props.task_rules,
      parameters: null as null | Record<string, string>,
      parameterOptions: null as null | Record<string, string[]>,
      joinConfig: initialJoin.draft,
      joinExtras: initialJoin.extras,
      parents: initialJoin.parents as ParentDraft[],
      dateFormat: getDateFormat(),
      // Mutable copy of the rule id so a save-with-warnings on a new rule can promote
      // subsequent saves to postUpdateTask instead of double-creating.
      taskRuleID: props.selected_rule.TaskRuleID as number | null,
    });

    // Props mutate when the parent component reloads rules or re-fetches sensor-tag
    // options (new rule added, sensor edited). The one-shot copies above would otherwise
    // leave the Task-rule dropdown and tag pickers showing stale lists.
    watch(
      () => props.task_rules,
      (next) => {
        state.task_rules = next;
      },
    );
    watch(
      () => props.sensorTagOptions,
      (next) => {
        state.sensorTagOptions = next;
      },
    );

    const parentIdCounter = buildParentIdCounter(highestParentId(initialJoin.parents));

    const getPropertyForTask = (
      taskID: number | null,
      propertyName: "InputTable" | "Name" | "ParameterNames" | "TaskID" | "Documentation"
    ): string => {
      if (taskID === null) return "";
      const filtered = state.tasks.filter((task) => task.TaskID === taskID);
      return filtered[0] ? String(filtered[0][propertyName]) : "";
    };

    // True when the selected task is a @multi_data_task (input_table='task_runs_join').
    // Drives whether the modal shows the typed join-config section and validates those fields.
    const isJoinRule = computed(() => getPropertyForTask(state.TaskID, "InputTable") === "task_runs_join");

    // Task rules the user can pick as this parent's task_rule_id — excludes self (no
    // self-loop) and any other parent's current task_rule pick (deduplication is a
    // parser rule).
    const availableTaskRulesFor = (parent: ParentDraft) => {
      const selfId = props.selected_rule.TaskRuleID;
      const taken = new Set<number>();
      for (const other of state.parents) {
        if (other === parent) continue;
        if (other.kind === "task_rule" && other.task_rule_id !== null) taken.add(other.task_rule_id);
      }
      return state.task_rules
        .filter((r) => r.TaskRuleID !== null && r.TaskRuleID !== selfId && !taken.has(r.TaskRuleID as number))
        .slice()
        .sort((a, b) => (a.Name > b.Name ? 1 : -1));
    };

    const addParent = () => {
      state.parents.push(buildParentDraft(parentIdCounter.next()));
    };

    const removeParent = (index: number) => {
      state.parents.splice(index, 1);
    };

    const sortTasks = (tasks: Task[]) => {
      return tasks.sort((a: Task, b: Task) => (a.Name > b.Name ? 1 : -1));
    };

    const get_tasks = async () => {
      const data = await fetchTasks();
      state.tasks = sortTasks(data);
      state.parameterOptions = data
        .map(({ TaskID, ParameterNames }) => ({ [TaskID]: ParameterNames }))
        .reduce((a, b) => ({ ...a, ...b }), {});
      if (state.TaskID !== null) {
        updateParameterOptions(state.TaskID);
      }
    };

    // Task kwargs the scheduler injects at fire time for @multi_data_task rules — never
    // a rule-authored value, so hidden from the Parameters editor even when the task
    // function declares them in its signature. `_join` carries the fire's
    // parent_task_run_ids plus the partial flag; `_group_key` is the fire's group_key
    // dict mirrored at the top level so grep / dashboards can search on it. Both are
    // written by the scheduler at fire time regardless of what's set here, so letting
    // the user edit them in the UI would be confusing.
    const RESERVED_TASK_KWARGS = new Set(["_join", "_group_key"]);

    const updateParameterOptions = (TaskID: number) => {
      if (state.parameterOptions) {
        const visibleKwargs = state.parameterOptions[TaskID].filter((k) => !RESERVED_TASK_KWARGS.has(k));
        let newParameters = visibleKwargs.reduce((a, b) => ({ ...a, [b]: null }), {}) as Record<string, string>;
        Object.keys(newParameters).map((key) => {
          if (key in props.selected_rule.Parameters) {
            newParameters[key] = JSON.stringify(props.selected_rule.Parameters[key]);
          }
        });
        state.parameters = newParameters;
      }
    };

    const isValidJSON = (jsonInput: string) => {
      try {
        JSON.parse(jsonInput);
        return true;
      } catch (error) {
        return false;
      }
    };

    /**
     * Takes null or parameters object with strings or null as values and returns parameters as JSON.
     * Removes empty parameters.
     */
    const formattedParameters = (parameters: null | Record<string, string>) =>
      parameters === null
        ? {}
        : Object.entries(parameters).reduce((a, [b, c]) => (c !== null ? { ...a, [b]: JSON.parse(c) } : a), {});

    const clearSelectedRule = () => {
      emit("clearSelectedRule", null);
    };

    // v-model.number on a cleared input yields "" (or NaN in some builds), not null.
    // Coerce that back to null so "left blank" is uniformly represented before validation.
    const asOptionalNumber = (v: number | null): number | null => {
      if (v === null) return null;
      if (typeof v === "number" && Number.isFinite(v)) return v;
      return null;
    };

    // Mirror ga11ium.tasks.join_config's parser rules so the user gets an inline error
    // instead of a backend 400 whenever possible.
    const validateJoin = (parents: ParentDraft[], draft: JoinConfigDraft): string => {
      if (parents.length === 0) return "Add at least one parent source before saving.";

      const filterToSame = draft.filter_to_same;
      for (const tag of filterToSame) {
        if (!JOIN_CONFIG_TAG_KEY_RE.test(tag)) {
          return `filter_to_same tag "${tag}" must match [A-Za-z0-9_]+`;
        }
      }
      const filterSet = new Set(filterToSame);
      const ids = new Set(parents.map((p) => p.id));
      // Broadcast rows are hidden and not saved in dynamic mode, so they aren't validated either.
      const actionsOf = (p: ParentDraft): TagActionRowDraft[] => (draft.share_grandparent ? [] : p.tagActions);

      for (const p of parents) {
        if (!PARENT_KIND_OPTIONS.includes(p.kind)) {
          return `Parent P${p.id}: invalid kind "${p.kind}"`;
        }
        if (p.kind === "task_rule" && (p.task_rule_id === null || !Number.isFinite(p.task_rule_id))) {
          return `Parent P${p.id}: task_rule parents need a task-rule selection.`;
        }
        const seen = new Set<string>();
        for (const row of actionsOf(p)) {
          if (!row.tag) return `Parent P${p.id}: an empty tag key in Sensor Tags Broadcast.`;
          if (!JOIN_CONFIG_TAG_KEY_RE.test(row.tag)) {
            return `Parent P${p.id}: tag "${row.tag}" must match [A-Za-z0-9_]+`;
          }
          if (filterSet.has(row.tag)) {
            return `Parent P${p.id}: tag "${row.tag}" is already in the shared filter_to_same — remove one.`;
          }
          if (seen.has(row.tag)) {
            return `Parent P${p.id}: duplicate tag "${row.tag}" in Sensor Tags Broadcast.`;
          }
          seen.add(row.tag);
          if (row.action === "match") {
            const pn = typeof row.pn === "number" ? row.pn : Number(row.pn);
            if (!Number.isInteger(pn) || pn <= 0) {
              return `Parent P${p.id}: Match on "${row.tag}" needs a target parent.`;
            }
            if (pn === p.id) {
              return `Parent P${p.id}: Match on "${row.tag}" cannot reference itself.`;
            }
            if (!ids.has(pn)) {
              return `Parent P${p.id}: Match on "${row.tag}" targets P${pn}, which isn't in the parents list.`;
            }
          }
        }
      }

      // Cycle detection per tag: walk each Match edge and error if we ever revisit
      // the starting parent along the same tag.
      const parentsById = new Map<number, ParentDraft>(parents.map((p) => [p.id, p] as const));
      for (const p of parents) {
        for (const row of actionsOf(p)) {
          if (row.action !== "match") continue;
          const startId = p.id;
          const tag = row.tag;
          const visited = new Set<number>([startId]);
          let currentId: number | null = Number(row.pn);
          while (currentId !== null) {
            if (visited.has(currentId)) {
              return `Parent P${startId}: Match on "${tag}" forms a cycle.`;
            }
            visited.add(currentId);
            const next = parentsById.get(currentId);
            const nextRow = next ? actionsOf(next).find((r) => r.tag === tag) : undefined;
            currentId = nextRow && nextRow.action === "match" ? Number(nextRow.pn) : null;
          }
        }
      }

      const timeout = asOptionalNumber(draft.timeout_minutes);
      if (timeout === null || !Number.isInteger(timeout) || timeout <= 0) {
        return "timeout_minutes must be a positive integer";
      }
      const fraction = asOptionalNumber(draft.min_success_fraction);
      if (fraction === null || !(fraction > 0 && fraction <= 1)) {
        return "min_success_fraction must be a number in (0, 1]";
      }
      if (draft.share_grandparent) {
        const gp = asOptionalNumber(draft.grandparent_timeout_minutes);
        if (gp === null || !Number.isInteger(gp) || gp <= 0) {
          return "grandparent_timeout_minutes must be a positive integer when share_grandparent is enabled";
        }
      }
      const grace = asOptionalNumber(draft.retry_grace_minutes);
      if (grace !== null && (!Number.isInteger(grace) || grace < 0)) {
        return "retry_grace_minutes must be a non-negative integer";
      }

      // Backend rule: a join rule needs at least one grouping dimension — either a
      // shared filter_to_same entry, a per-parent Split/Match row, or share_grandparent.
      // Without one the rule has no basis to divide events into groups.
      const hasAnyGroupingDim =
        filterToSame.length > 0 ||
        parents.some((p) => actionsOf(p).length > 0) ||
        draft.share_grandparent;
      if (!hasAnyGroupingDim) {
        return (
          "Add at least one grouping dimension: a Match Parents entry, a per-parent " +
          "Sensor Tags Broadcast row (Split/Match), or enable share_grandparent."
        );
      }

      // Backend rule: share_grandparent only makes sense when every parent is itself a
      // task_rule child under a shared spawner. Raw-source parents (bursts/data/results)
      // have no producing task_run and no grandparent to align on.
      if (draft.share_grandparent) {
        for (const p of parents) {
          if (p.kind !== "task_rule") {
            return (
              `Parent P${p.id}: share_grandparent is incompatible with raw parents ` +
              `(${p.kind}) — raw-source rows have no producing task_run to group by.`
            );
          }
        }
      }

      // Backend rule: parents of the same raw kind are told apart only by their sensor
      // filters, so each needs one. The API also rejects filters that overlap on real sensors.
      for (const kind of ["bursts", "data", "results"]) {
        const sameKind = parents.filter((p) => p.kind === kind);
        if (sameKind.length < 2) continue;
        const unfiltered = sameKind.find((p) => !p.tagSets.some((s) => Object.keys(s.tags).length > 0));
        if (unfiltered) {
          return (
            `Parent P${unfiltered.id}: a rule with more than one ${kind} parent needs a Sensor Tags Filter ` +
            `on each, so every ${kind} row maps to exactly one parent.`
          );
        }
      }
      return "";
    };

    // Serialize the shared join-config draft back into a Parameters-shaped object.
    // Optional numeric fields are omitted when null so a "leave default" entry doesn't
    // get written as an explicit null — the parser defaults own the fallback.
    const serializeJoinConfig = (draft: JoinConfigDraft): Record<string, unknown> => {
      const out: Record<string, unknown> = {
        filter_to_same: [...draft.filter_to_same],
        timeout_minutes: asOptionalNumber(draft.timeout_minutes),
        min_success_fraction: asOptionalNumber(draft.min_success_fraction),
      };
      if (draft.share_grandparent) {
        out.share_grandparent = true;
        out.grandparent_timeout_minutes = asOptionalNumber(draft.grandparent_timeout_minutes);
      }
      const grace = asOptionalNumber(draft.retry_grace_minutes);
      if (grace !== null) out.retry_grace_minutes = grace;
      return out;
    };

    const save_rule = async () => {
      state.errorMessage = "";
      state.warnings = [];
      let newParameters: Record<string, unknown> = {};
      try {
        newParameters = formattedParameters(state.parameters);
      } catch (error) {
        state.errorMessage = "Invalid JSON for parameters";
        return;
      }

      if (!state.Name) {
        state.errorMessage = "You must specify a name";
        return;
      }
      if (!state.TaskID) {
        state.errorMessage = "You must choose a task";
        return;
      }
      if (state.tagSets.some((d) => d.selectTag || d.selectTagValue)) {
        state.errorMessage = "You have not finished adding a sensor tag";
        return;
      }

      if (isJoinRule.value) {
        // Unfinished per-parent AND/OR picker rows — same gate as the rule-level picker.
        for (const p of state.parents) {
          if (p.tagSets.some((s) => s.selectTag || s.selectTagValue)) {
            state.errorMessage = `Parent P${p.id}: finish adding the current sensor tag first`;
            return;
          }
        }
        const joinErr = validateJoin(state.parents, state.joinConfig);
        if (joinErr) {
          state.errorMessage = joinErr;
          return;
        }
        // Merge order: joinExtras is the forward-compat passthrough baseline (any
        // unknown keys the parser left alone at load time), then newParameters so the
        // user's edits to the Parameters editor win, then the typed join-config, then
        // the parents list. Without extras-first, user edits in state.parameters would
        // be silently reverted to the loaded extras values.
        newParameters = {
          ...state.joinExtras,
          ...newParameters,
          ...serializeJoinConfig(state.joinConfig),
          parents: state.parents.map((p) => serializeParent(p, state.joinConfig.share_grandparent)),
        };
      }

      // Discard empty sets, then send a plain dict for one set or an array for multiple.
      const nonEmpty = state.tagSets.map((d) => d.tags).filter((t) => Object.keys(t).length > 0);
      const SensorTags = nonEmpty.length === 0 ? null : nonEmpty.length === 1 ? nonEmpty[0] : nonEmpty;

      const isNewRule = state.taskRuleID === null;

      let newTask = {
        Name: state.Name,
        TaskID: state.TaskID,
        SensorIDs: props.selected_rule.SensorIDs,
        SensorTags,
        // The backend derives DataTaskRuleIDs from parents for join rules — don't
        // send it here or we'll fight the derivation.
        DataTaskRuleIDs: isJoinRule.value ? null : state.DataTaskRuleIDs,
        StartTime: jsTimeToRealEpoch(state.pickerStartDate),
        Enabled: state.enabled,
        MultipleTruePerBatch: state.multipleTruePerBatch,
        Parameters: newParameters,
        MaxConcurrent:
          state.maxConcurrent === "" || state.maxConcurrent === null ? null : Number(state.maxConcurrent),
      } as unknown as TaskRule;

      if (state.endDate && state.pickerEndDate) {
        newTask.EndTime = jsTimeToRealEpoch(state.pickerEndDate);
      } else if (!state.endDate && props.selected_rule.EndTime) {
        newTask.EndTime = null;
      }

      let result: { task_rule_id: number | null; warnings: string[] } | null = null;
      try {
        if (isNewRule) {
          result = await postNewTask(newTask);
        } else {
          if (!state.taskRuleID) {
            state.errorMessage = "You've not supplied a task rule ID";
            return;
          }
          result = await postUpdateTask(newTask, state.taskRuleID);
        }
      } catch (error) {
        if (error instanceof Error) {
          state.errorMessage = `Error adding/updating task: ${error.message ? error.message : ""}`;
        } else {
          state.errorMessage = "Error adding/updating task";
        }
        return;
      }
      // Capture the server-assigned id so a save-with-warnings on a new rule doesn't
      // trigger a duplicate create on the next click — subsequent saves now hit
      // postUpdateTask against this id.
      if (isNewRule && result && typeof result.task_rule_id === "number") {
        state.taskRuleID = result.task_rule_id;
      }
      // The write succeeded either way; refresh the parent list. When the API returned
      // warnings, keep the modal open so the user actually sees them before dismissing.
      emit("getTaskRules");
      if (result && result.warnings.length > 0) {
        state.warnings = result.warnings;
        return;
      }
      emit("clearSelectedRule", null);
    };

    const push_end_date = () => {
      if (state.pickerEndDate) {
        if (state.pickerStartDate > state.pickerEndDate) {
          state.pickerEndDate = getNextDayFromDate(state.pickerStartDate);
        }
      }
    };

    get_tasks();

    return {
      ...toRefs(state),
      isJoinRule,
      parentKindOptions: PARENT_KIND_OPTIONS,
      availableTaskRulesFor,
      addParent,
      removeParent,
      clearSelectedRule,
      save_rule,
      getPropertyForTask,
      updateParameterOptions,
      isValidJSON,
      push_end_date,
    };
  },
});
